/* eslint-disable @typescript-eslint/no-explicit-any */
// src/pages/Login.tsx
import { useState, useEffect } from 'react';
import {Helmet} from "react-helmet-async";
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle, RotateCcw, Mail } from 'lucide-react';

// Use environment variable for API base URL
const API_BASE = import.meta.env.VITE_API_BASE;

export default function Login() {
  const {
    login,
    lockStatus,
    countdown,
    clearLockStatus,
    requestAccountReactivation,
  } = useAuth();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(
    null,
  );
  const [accountDeactivated, setAccountDeactivated] = useState(false);
  const [reactivationRequested, setReactivationRequested] = useState(false);
  const [showReactivationForm, setShowReactivationForm] = useState(false);
  const [deactivationDetails, setDeactivationDetails] = useState<{
    deactivatedAt?: string;
    deactivationType?: string;
    canBeReactivated?: boolean;
  }>({});

  // Clear errors and reset states when email changes
  useEffect(() => {
    setError('');
    setRemainingAttempts(null);
    setAccountDeactivated(false);
    setReactivationRequested(false);
    setShowReactivationForm(false);
    setDeactivationDetails({});
  }, [email]);

  // Update remaining attempts from lock status
  useEffect(() => {
    if (lockStatus.message && !lockStatus.locked) {
      const match = lockStatus.message.match(/(\d+) attempt/);
      if (match) {
        setRemainingAttempts(parseInt(match[1]));
      }
    }
  }, [lockStatus]);

  // Auto-show reactivation form when accountDeactivated is detected
  useEffect(() => {
    if (accountDeactivated) {
      setShowReactivationForm(true);
    }
  }, [accountDeactivated]);

  const resetForm = () => {
    setAccountDeactivated(false);
    setReactivationRequested(false);
    setShowReactivationForm(false);
    setError('');
    setEmail('');
    setPassword('');
    setLoading(false);
    setRemainingAttempts(null);
    setDeactivationDetails({});
    console.log('Form reset completed');
  };

  // Helper function to check account status before login
  const checkAccountStatus = async (
    email: string,
  ): Promise<{ deactivated: boolean; details?: any }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/check-account-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        const data = await response.json();
        return {
          deactivated: data.deactivated === true,
          details: data,
        };
      }
      return { deactivated: false };
    } catch (error) {
      console.error('Error checking account status:', error);
      return { deactivated: false };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setRemainingAttempts(null);
    setAccountDeactivated(false);
    setShowReactivationForm(false);
    setDeactivationDetails({});

    try {
      // First, check if account is deactivated
      const statusCheck = await checkAccountStatus(email);
      if (statusCheck.deactivated) {
        setAccountDeactivated(true);
        setShowReactivationForm(true);
        setDeactivationDetails({
          deactivatedAt: statusCheck.details?.deactivatedAt,
          deactivationType: statusCheck.details?.deactivationType,
          canBeReactivated: statusCheck.details?.canBeReactivated,
        });
        setError(
          'Your account has been deactivated. Please request reactivation.',
        );
        setLoading(false);
        return;
      }

      // If not deactivated, proceed with normal login
      const result = await login(email, password);

      if (result.success) {
        // ✅ Reset form on successful login
        resetForm();

        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);

          // Redirect based on role
          if (parsedUser.role === 'admin') {
            navigate('/dashboard/admin-panel');
          } else {
            navigate('/find-job');
          }
        } else {
          navigate('/find-job');
        }
      } else {
        // Handle specific login result cases
        if (result.locked) {
          setError(
            `Account locked. Please try again in ${Math.ceil((result.retryAfter || 0) / 60)} minutes.`,
          );
        } else if (result.remainingAttempts !== undefined) {
          setRemainingAttempts(result.remainingAttempts);
          setError(
            `Invalid email or password. ${result.remainingAttempts} attempt${result.remainingAttempts !== 1 ? 's' : ''} remaining.`,
          );
        } else if (result.requiresVerification) {
          setError('Please verify your email before logging in.');
        } else if (result.accountDeactivated) {
          setAccountDeactivated(true);
          setShowReactivationForm(true);
          setDeactivationDetails({
            deactivatedAt: result.deactivatedAt,
            deactivationType: result.deactivationType,
            canBeReactivated: result.canBeReactivated,
          });

          // FIX: Handle null deactivationType - assume user_initiated if null
          const canReactivate =
            result.canBeReactivated !== false &&
            (result.deactivationType === 'user_initiated' ||
              result.deactivationType === null); // Allow null as user_initiated

          setError(
            canReactivate
              ? 'Your account has been deactivated. Please request reactivation.'
              : 'Your account has been deactivated by an administrator. Please contact support.',
          );
        } else {
          // Handle other errors
          setError(
            result.error?.toString() || 'Login failed. Please try again.',
          );
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);

      // Enhanced error handling for deactivated accounts
      if (err?.code === 'ACCOUNT_DEACTIVATED') {
        setAccountDeactivated(true);
        setShowReactivationForm(true);
        setDeactivationDetails({
          deactivatedAt: err.deactivatedAt,
          deactivationType: err.deactivationType,
          canBeReactivated: err.canBeReactivated,
        });

        // FIX: Handle null deactivationType in catch block too
        const canReactivate =
          err.canBeReactivated !== false &&
          (err.deactivationType === 'user_initiated' ||
            err.deactivationType === null);

        setError(
          canReactivate
            ? 'Your account has been deactivated. Please request reactivation.'
            : 'Your account has been deactivated by an administrator. Please contact support.',
        );
      } else if (
        err?.message?.includes('deactivated') ||
        err?.message?.includes('inactive')
      ) {
        setAccountDeactivated(true);
        setShowReactivationForm(true);
        setError(
          'Your account has been deactivated. Please request reactivation.',
        );
      } else {
        setError(err?.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReactivation = async () => {
    if (!email) {
      setError('Please enter your email address to request reactivation.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await requestAccountReactivation(email);
      setReactivationRequested(true);
      setShowReactivationForm(false);
      setError('');
    } catch (err: any) {
      setError(
        err.message || 'Failed to send reactivation request. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    resetForm();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleClearLock = () => {
    clearLockStatus();
    setError('');
    setRemainingAttempts(null);
  };

  // Determine if reactivation should be allowed
  const canReactivate =
    deactivationDetails.canBeReactivated !== false &&
    (deactivationDetails.deactivationType === 'user_initiated' ||
      deactivationDetails.deactivationType === null);

  // If we're showing reactivation form, don't show the login form
  const showLoginForm = !showReactivationForm && !reactivationRequested;

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-6 py-12 transition-colors duration-300 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 to-gray-800'
          : 'bg-gradient-to-br from-sky-50 to-blue-50'
      }`}
    >
      <div
        className={`w-full max-w-md shadow-lg rounded-2xl p-8 border transition-colors duration-300 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-sky-100'
        }`}
      >
        <Helmet>
          <title>Login to FindWork | Find Jobs in Kenya</title>
          <meta
            name="description"
            content="Log in to your FindWork account to find jobs, manage applications, and connect with employers near you in Kenya."
          />
          <link rel="canonical" href="https://findwork.ltd/login" />
        </Helmet>
        
        {/* Header */}
        <h1 className="text-3xl font-bold text-center bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
          {showReactivationForm || reactivationRequested
            ? 'Reactivate Account'
            : 'Welcome Back'}
        </h1>
        <p
          className={`text-center mt-2 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          {showReactivationForm || reactivationRequested
            ? 'Request account reactivation'
            : 'Login to continue your job search'}
        </p>

        {/* Account Locked Banner */}
        {lockStatus.locked && countdown && showLoginForm && (
          <div
            className={`mt-4 p-4 rounded-md border flex items-center space-x-3 ${
              isDark
                ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800'
                : 'bg-yellow-100 text-yellow-800 border-yellow-200'
            }`}
          >
            <Lock size={20} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Account Temporarily Locked</p>
              <p className="text-sm mt-1">
                Try again in {formatCountdown(countdown)}
              </p>
            </div>
            <button
              onClick={handleClearLock}
              className={`text-sm px-3 py-1 rounded border transition ${
                isDark
                  ? 'border-yellow-700 text-yellow-300 hover:bg-yellow-800/50'
                  : 'border-yellow-400 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              Clear
            </button>
          </div>
        )}

        {/* Reactivation Success Message */}
        {reactivationRequested && (
          <div
            className={`mt-4 p-4 rounded-md border flex items-center space-x-3 ${
              isDark
                ? 'bg-green-900/30 text-green-300 border-green-800'
                : 'bg-green-100 text-green-800 border-green-200'
            }`}
          >
            <Mail size={20} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Reactivation Request Sent</p>
              <p className="text-sm mt-1">
                Check your email for reactivation instructions. You'll receive a
                link to set a new password and reactivate your account.
              </p>
              <button
                onClick={handleBackToLogin}
                className={`mt-3 px-4 py-2 rounded-md text-sm font-medium transition ${
                  isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Back to Login
              </button>
            </div>
          </div>
        )}

        {/* Account Deactivated Reactivation Form */}
        {accountDeactivated &&
          showReactivationForm &&
          !reactivationRequested && (
            <div
              className={`mt-4 p-4 rounded-md border ${
                isDark
                  ? canReactivate
                    ? 'bg-orange-900/30 border-orange-800 text-orange-300'
                    : 'bg-red-900/30 border-red-800 text-red-300'
                  : canReactivate
                    ? 'bg-orange-100 border-orange-200 text-orange-800'
                    : 'bg-red-100 border-red-200 text-red-800'
              }`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <RotateCcw size={20} className="flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    {canReactivate
                      ? 'Account Deactivated'
                      : 'Account Suspended'}
                  </p>
                  <p className="text-sm mt-1">
                    {canReactivate
                      ? 'Your account has been deactivated. Request reactivation to restore access.'
                      : 'Your account has been suspended. Please contact support for assistance.'}
                  </p>
                  {deactivationDetails.deactivatedAt && (
                    <p className="text-xs mt-1 opacity-75">
                      Deactivated on:{' '}
                      {formatDate(deactivationDetails.deactivatedAt)}
                    </p>
                  )}
                </div>
              </div>

              {canReactivate ? (
                <div className="space-y-3">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-orange-200 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Enter your email"
                    />
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={handleRequestReactivation}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-md text-sm font-medium hover:from-orange-600 hover:to-red-700 disabled:opacity-50 transition"
                    >
                      {loading ? 'Sending...' : 'Request Reactivation'}
                    </button>
                    <button
                      onClick={handleBackToLogin}
                      className={`px-4 py-2 border rounded-md text-sm font-medium transition ${
                        isDark
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm mb-4">
                    This account has been suspended and cannot be reactivated
                    automatically.
                  </p>
                  <button
                    onClick={handleBackToLogin}
                    className={`px-6 py-2 border rounded-md text-sm font-medium transition ${
                      isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Back to Login
                  </button>
                </div>
              )}
            </div>
          )}

        {/* Remaining Attempts Warning */}
        {remainingAttempts !== null &&
          remainingAttempts < 5 &&
          !lockStatus.locked &&
          !accountDeactivated &&
          showLoginForm && (
            <div
              className={`mt-4 p-4 rounded-md border flex items-center space-x-3 ${
                isDark
                  ? 'bg-orange-900/30 text-orange-300 border-orange-800'
                  : 'bg-orange-100 text-orange-800 border-orange-200'
              }`}
            >
              <AlertCircle size={20} className="flex-shrink-0" />
              <div>
                <p className="font-medium">Security Notice</p>
                <p className="text-sm mt-1">
                  {remainingAttempts} attempt
                  {remainingAttempts !== 1 ? 's' : ''} remaining before account
                  lock.
                </p>
              </div>
            </div>
          )}

        {/* Error Message - Only show for login form errors */}
        {error &&
          !lockStatus.locked &&
          !accountDeactivated &&
          showLoginForm && (
            <div
              className={`mt-4 p-3 rounded-md border flex items-center space-x-2 ${
                isDark
                  ? 'bg-red-900/30 text-red-300 border-red-800'
                  : 'bg-red-100 text-red-700 border-red-200'
              }`}
            >
              <AlertCircle size={18} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

        {/* Login Form - Only show if not dealing with reactivation */}
        {showLoginForm && (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            {/* Email */}
            <div>
              <label
                className={`block font-medium mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={lockStatus.locked || loading}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 disabled:bg-gray-800 disabled:text-gray-500'
                    : 'bg-white border-sky-200 text-gray-900 placeholder-gray-500 disabled:bg-gray-100 disabled:text-gray-400'
                }`}
                placeholder="example@email.com"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <label
                className={`block font-medium mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={lockStatus.locked || loading}
                className={`w-full px-4 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 disabled:bg-gray-800 disabled:text-gray-500'
                    : 'bg-white border-sky-200 text-gray-900 placeholder-gray-500 disabled:bg-gray-100 disabled:text-gray-400'
                }`}
                placeholder="********"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                disabled={lockStatus.locked || loading}
                className={`absolute right-3 top-9 transition ${
                  isDark
                    ? 'text-gray-400 hover:text-sky-400 disabled:text-gray-600'
                    : 'text-gray-500 hover:text-sky-600 disabled:text-gray-400'
                }`}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              <div className="flex justify-end mt-2">
                <Link
                  to="/forgot-password"
                  className={`text-sm bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent hover:underline font-medium ${
                    lockStatus.locked ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || lockStatus.locked}
              className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-md hover:from-sky-600 hover:to-blue-700 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Logging in...
                </span>
              ) : lockStatus.locked ? (
                'Account Locked'
              ) : (
                'Login'
              )}
            </button>
          </form>
        )}

        {/* Footer Links */}
        {showLoginForm && (
          <p
            className={`text-center mt-6 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            Don't have an account?{' '}
            <Link
              to="/signup"
              className={`bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent font-medium hover:underline ${
                lockStatus.locked ? 'opacity-50 pointer-events-none' : ''
              }`}
            >
              Sign Up
            </Link>
          </p>
        )}

        {/* Security Tips */}
        {showLoginForm && (
          <div
            className={`mt-6 p-4 rounded-md border text-sm ${
              isDark
                ? 'bg-gray-700/50 border-gray-600 text-gray-400'
                : 'bg-sky-50 border-sky-200 text-gray-600'
            }`}
          >
            <p className="font-medium mb-2">💡 Security Tips:</p>
            <ul className="space-y-1">
              <li>• Use a strong, unique password</li>
              <li>• Don't share your login credentials</li>
              <li>• Log out from shared devices</li>
              <li>• Account locks after 5 failed attempts</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
